import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, Underline as U, Strikethrough, Heading2, Heading3, List, ListOrdered, Quote, Minus, Link2, ImagePlus, Undo2, Redo2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, htmlText } from "@/lib/api";

const Btn = ({ on, active, title, testId, children, disabled }) => (
  <button type="button" title={title} aria-label={title} data-testid={testId} disabled={disabled}
    onMouseDown={(e) => e.preventDefault()} onClick={on}
    className={`h-8 w-8 inline-flex items-center justify-center rounded-sm transition-colors ${active ? "bg-[#0B2A5B] text-white" : "hover:bg-blue-50 text-zinc-700"} disabled:opacity-40`}>
    {children}
  </button>
);

const Sep = () => <span className="w-px h-5 bg-zinc-200 mx-1" />;

const Toolbar = ({ editor, prefix }) => {
  const file = useRef();
  const [busy, setBusy] = useState(false);
  const c = () => editor.chain().focus();

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/desk/upload", fd);
      c().setImage({ src: data.url, alt: "" }).run();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const link = () => {
    const prev = editor.getAttributes("link").href || "https://";
    const url = window.prompt("Link URL (leave empty to remove)", prev);
    if (url === null) return;
    if (!url.trim()) c().unsetLink().run();
    else if (/^https?:\/\//i.test(url.trim())) c().setLink({ href: url.trim(), target: "_blank" }).run();
    else toast.error("Links must start with http:// or https://");
  };

  return (
    <div data-testid={`${prefix}-toolbar`} className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 border-b border-zinc-200 bg-white/95 backdrop-blur px-1.5 py-1">
      <Btn testId={`${prefix}-bold`} title="Bold (Ctrl+B)" active={editor.isActive("bold")} on={() => c().toggleBold().run()}><Bold className="h-4 w-4" /></Btn>
      <Btn testId={`${prefix}-italic`} title="Italic (Ctrl+I)" active={editor.isActive("italic")} on={() => c().toggleItalic().run()}><Italic className="h-4 w-4" /></Btn>
      <Btn testId={`${prefix}-underline`} title="Underline (Ctrl+U)" active={editor.isActive("underline")} on={() => c().toggleUnderline().run()}><U className="h-4 w-4" /></Btn>
      <Btn testId={`${prefix}-strike`} title="Strikethrough" active={editor.isActive("strike")} on={() => c().toggleStrike().run()}><Strikethrough className="h-4 w-4" /></Btn>
      <Sep />
      <Btn testId={`${prefix}-h2`} title="Section heading" active={editor.isActive("heading", { level: 2 })} on={() => c().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></Btn>
      <Btn testId={`${prefix}-h3`} title="Sub-heading" active={editor.isActive("heading", { level: 3 })} on={() => c().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></Btn>
      <Sep />
      <Btn testId={`${prefix}-bullets`} title="Bullet list" active={editor.isActive("bulletList")} on={() => c().toggleBulletList().run()}><List className="h-4 w-4" /></Btn>
      <Btn testId={`${prefix}-numbers`} title="Numbered list" active={editor.isActive("orderedList")} on={() => c().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></Btn>
      <Btn testId={`${prefix}-quote`} title="Quote" active={editor.isActive("blockquote")} on={() => c().toggleBlockquote().run()}><Quote className="h-4 w-4" /></Btn>
      <Btn testId={`${prefix}-hr`} title="Divider" on={() => c().setHorizontalRule().run()}><Minus className="h-4 w-4" /></Btn>
      <Sep />
      <Btn testId={`${prefix}-link`} title="Link" active={editor.isActive("link")} on={link}><Link2 className="h-4 w-4" /></Btn>
      <Btn testId={`${prefix}-image`} title="Insert image" disabled={busy} on={() => file.current.click()}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
      </Btn>
      <input ref={file} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={upload} data-testid={`${prefix}-image-file`} />
      <Sep />
      <Btn testId={`${prefix}-undo`} title="Undo" disabled={!editor.can().undo()} on={() => c().undo().run()}><Undo2 className="h-4 w-4" /></Btn>
      <Btn testId={`${prefix}-redo`} title="Redo" disabled={!editor.can().redo()} on={() => c().redo().run()}><Redo2 className="h-4 w-4" /></Btn>
    </div>
  );
};

export const RichEditor = ({ value, onChange, testId, placeholder, className = "" }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer nofollow" } }),
      Image,
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => onChange(ed.isEmpty ? "" : ed.getHTML()),
    editorProps: { attributes: { class: `article-body ProseMirror-editor min-h-[420px] px-4 py-4 focus:outline-none ${className}`, "data-testid": testId } },
  });

  useEffect(() => {
    if (editor && (value || "") !== (editor.isEmpty ? "" : editor.getHTML())) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;
  const words = htmlText(editor.getHTML()).split(" ").filter(Boolean).length;
  return (
    <div className="border border-zinc-300 rounded-sm bg-white focus-within:border-[#1D4ED8] transition-colors">
      <Toolbar editor={editor} prefix={testId} />
      <div className="max-h-[75vh] overflow-y-auto"><EditorContent editor={editor} /></div>
      <p data-testid={`${testId}-wordcount`} className="border-t border-zinc-100 px-3 py-1.5 text-[11px] text-zinc-500 font-mono-meta">{words} words</p>
    </div>
  );
};
